











import {Component, Vue, Watch} from "vue-property-decorator";
import PasswordlessService from "@/components/domains/auth/passwordlessService";
import PasskeyService from "@/components/domains/auth/passkeyService";
import {offerDismissKey, shouldOfferPasskey} from "@/components/domains/auth/webauthnJson";
import {AuthGetterTypes} from "@/store/auth/types";

function readDismissed(userId: string): boolean {
  try {
    return window.localStorage.getItem(offerDismissKey(userId)) === '1';
  } catch (e) {
    return false;
  }
}

function writeDismissed(userId: string): void {
  try {
    window.localStorage.setItem(offerDismissKey(userId), '1');
  } catch (e) {
    // private mode etc. — hidden until the next page load only
  }
}

/**
 * One-time offer right after signing in: "次回から、指紋や顔でログインできる
 * ようにしますか？". Only when the backend offers it, this browser and device
 * can do it, the user has no device registered yet, and they have not
 * answered before on this browser. Renders nothing otherwise.
 */
@Component
export default class PasskeyOfferBanner extends Vue {
  name = "PasskeyOfferBanner";

  offer = false;
  dismissed = false;
  checkedFor = '';

  get userId(): string {
    const u = this.$store.getters[AuthGetterTypes.getCurrentUser];
    return u ? String(u.id || u.userId || '') : '';
  }

  get visible(): boolean {
    return this.offer && !this.dismissed && this.$route.path !== '/passkeys';
  }

  mounted(): void {
    this.$root.$on('passkey-registered', this.onRegistered);
    this.check();
  }

  beforeDestroy(): void {
    this.$root.$off('passkey-registered', this.onRegistered);
  }

  @Watch('userId')
  onUser(): void {
    this.check();
  }

  async check(): Promise<void> {
    const userId = this.userId;
    if (!userId || this.checkedFor === userId) return;
    this.checkedFor = userId;
    this.offer = false;
    if (!PasskeyService.isSupported() || readDismissed(userId)) return;

    const policy = await PasswordlessService.loadPolicy();
    if (!policy.passkeyAvailable) return;
    const platform = await PasskeyService.hasPlatformAuthenticator();
    if (!platform) return;
    let count: number | null = null;
    try {
      count = (await PasskeyService.list()).length;
    } catch (e) {
      count = null;
    }
    if (userId !== this.userId) return; // signed out / switched meanwhile
    this.offer = shouldOfferPasskey({
      passkeyAvailable: policy.passkeyAvailable,
      supported: true,
      platformAuthenticator: platform,
      dismissed: readDismissed(userId),
      existingCount: count,
    });
  }

  onRegistered(): void {
    this.answer();
  }

  answer(): void {
    this.dismissed = true;
    if (this.userId) writeDismissed(this.userId);
  }

  setUp(): void {
    this.answer();
    if (this.$route.path !== '/passkeys') this.$router.push('/passkeys');
  }

  later(): void {
    this.answer();
  }
}
